import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { RequestWalletFigure } from '../ClientRequests/RequestFundingSummary';
import './wallet.css';

/**
 * P5 — CLIENTS WHOSE AVAILABLE BALANCE IS ALREADY BELOW ZERO.
 *
 * THE GAP THIS CLOSES. walletFloor stops NEW breaches: a request that would take
 * available below zero is refused at submit and the approve button is disabled.
 * It does nothing about the ones that are already there — balances that went
 * negative before the floor landed, or through a path the floor does not gate
 * (a price adjustment raised after the fact, a payout claimed against a hold
 * that later grew). Those clients exist, every spend of theirs fails, and until
 * now NOTHING in the product enumerated them. Staff found out when a client rang
 * up asking why their booking would not go through.
 *
 * WHAT THIS IS NOT. It is not a fix and it must never be turned into one. An
 * available balance below zero is resolved by topping up or by releasing a
 * commitment — never by spending more of it — so there is deliberately no
 * "override" or "force" action here. The list is a worklist: who, how short,
 * and what is holding it.
 *
 * ── THE HONESTY RULE ─────────────────────────────────────────────────────────
 * The money contract cuts both ways: we must not claim a client is in trouble
 * without evidence, AND we must not claim everyone is fine without evidence.
 * `available_balance` is a computed figure (total − reserved − pending payouts),
 * NOT a stored column, so a source that does not carry it cannot answer this
 * question at all. Two sources are tried, in order:
 *
 *   1. GET /api/wallet/clients/negative-available — the dedicated endpoint,
 *      which computes availability per client server-side.
 *   2. GET /api/wallet/clients — the overview list. Used ONLY if its rows
 *      actually carry an `available_balance` field.
 *
 * If neither can answer, this renders an explicit "cannot be checked here"
 * notice. It NEVER renders "all clear" off a source that has no idea — an empty
 * green panel over a client who cannot spend is the exact failure this exists
 * to end.
 */

const money = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return '—';
    return `AED ${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Below −0.005, i.e. genuinely negative rather than a rounding artefact of zero.
const isBelowZero = (row) => Number(row?.available_balance) < -0.005;

// Both a bare array and a { clients: [...] } envelope are accepted — the
// endpoint is landing in the same wave and this component must not be the
// reason a correct payload renders nothing.
const readRows = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.clients)) return data.clients;
    return null;
};

export default function NegativeBalanceWorklist({ onCountChange }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    // 'dedicated' | 'overview' | null. null means NO source could answer, which
    // is a different statement from "answered, and the list is empty".
    const [source, setSource] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/wallet/clients/negative-available');
            const list = readRows(res.data);
            if (list) {
                setRows(list.filter(isBelowZero));
                setSource('dedicated');
                setLoading(false);
                return;
            }
        } catch (err) {
            // 404 = this server predates the endpoint; anything else is worth a
            // log but is handled identically, because the fallback below is
            // strictly better than showing nothing.
            if (err.response?.status !== 404) {
                console.error('Error fetching negative-available clients:', err);
            }
        }

        try {
            const res = await axios.get('/api/wallet/clients');
            const list = readRows(res.data) || [];
            // THE FIELD TEST, not a row test. Every row lacking the field could
            // mean "nobody is negative" or "this endpoint does not compute
            // availability" — and those must not render the same way.
            const carriesAvailability = list.some((c) => c && c.available_balance !== undefined && c.available_balance !== null);
            if (carriesAvailability) {
                setRows(list.filter(isBelowZero));
                setSource('overview');
                return;
            }
            setRows([]);
            setSource(null);
        } catch (err) {
            console.error('Error fetching client wallets for the negative worklist:', err);
            setRows([]);
            setSource(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => { if (!cancelled) await load(); })();
        return () => { cancelled = true; };
    }, [load]);

    useEffect(() => {
        if (!loading && onCountChange) onCountChange(rows.length);
    }, [loading, rows.length, onCountChange]);

    if (loading) return null;

    // Nothing below zero: the overview row already says so (owner,
    // 2026-09-07) — this block used to render an all-clear card here.
    if (source !== null && rows.length === 0) return null;

    // NO SOURCE COULD ANSWER. Say exactly that.
    if (source === null) {
        return (
            <div className="swallet__negative swallet__negative--unknown">
                <h3 className="swallet__negativeTitle">Clients below zero available</h3>
                <div className="swallet__negativeBody">
                    This server does not report available balances in a list, so clients whose available
                    balance is already below zero <strong>cannot be checked here</strong>. This is not an
                    all-clear — open a client to see their availability. (The floor still prevents new
                    breaches; it does not clear existing ones.)
                </div>
            </div>
        );
    }

    const shortfall = rows.reduce((s, r) => s + Math.abs(Number(r.available_balance) || 0), 0);

    return (
        <div className="swallet__negative">
            <div className="swallet__negativeHead">
                <h3 className="swallet__negativeTitle">
                    Clients below zero available · {rows.length}
                </h3>
                <span className="swallet__sectionSub">
                    {money(shortfall)} short in total. Every one of these clients has spending that will
                    fail until they top up or a commitment is released. The wallet floor stops new
                    breaches — it does not clear these.
                </span>
            </div>

            <div className="swallet__negativeList">
                {rows.map((c) => (
                    <div className="swallet__negativeRow" key={c.client_id}>
                        <div className="swallet__negativeRowHead">
                            <Link to={`/wallet/clients/${c.client_id}`} className="swallet__proofLink">
                                {c.display_name || `Client #${c.client_id}`}
                            </Link>
                            <span className="swallet__negativeShort">
                                {money(Math.abs(Number(c.available_balance) || 0))} short
                            </span>
                        </div>
                        {/* The same component the review modals use, so the
                            explanation of a negative — and the committed
                            breakdown behind its expand — is worded identically
                            everywhere it appears. */}
                        <RequestWalletFigure wallet={c} />
                    </div>
                ))}
            </div>
        </div>
    );
}
